import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Container, Alert, Button, Form, ProgressBar, Spinner, Table } from "react-bootstrap";
import { FaAngleUp, FaAngleDown } from "react-icons/fa";
import PageHeader from "./PageHeader";
import PaginationComponent from "./PaginationComponent";

const AmoSyncPage = () => {
  const { isAuthenticated, hasRole } = useAuth() || {};
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const [error, setError] = useState(null);
  const [syncJobId, setSyncJobId] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [syncStarting, setSyncStarting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState(null);
  const [importError, setImportError] = useState(null);
  const [importSkipped, setImportSkipped] = useState([]);
  const [mergeLoadingKey, setMergeLoadingKey] = useState(null);
  const [allDeals, setAllDeals] = useState([]);
  const [missingDeals, setMissingDeals] = useState([]);
  const [partnerByPromo, setPartnerByPromo] = useState({});
  const [availablePipelines, setAvailablePipelines] = useState([]);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [selectedPipelines, setSelectedPipelines] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedDealIds, setSelectedDealIds] = useState([]);
  const [forceImportIds, setForceImportIds] = useState([]);
  const [completedJobId, setCompletedJobId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState("created_ts");
  const [sortOrder, setSortOrder] = useState("desc");

  const toISODate = (date) => date.toISOString().slice(0, 10);
  const defaultEnd = useMemo(() => new Date(), []);
  const defaultStart = useMemo(() => {
    const copy = new Date();
    copy.setDate(copy.getDate() - 29);
    return copy;
  }, []);
  const initialRange = {
    startDate: toISODate(defaultStart),
    endDate: toISODate(defaultEnd),
  };
  const [dateRange, setDateRange] = useState(initialRange);
  const [pendingRange, setPendingRange] = useState(initialRange);
  const [rangeError, setRangeError] = useState(null);
  const filteredDeals = useMemo(() => {
    return allDeals.filter((deal) => {
      const hasPromoCode = Boolean(String(deal.promo_code || "").trim());
      const notInDb = !deal.exists_in_db;
      const pipelineAllowed = selectedPipelines.length === 0
        || selectedPipelines.includes(deal.pipeline_name || "Без воронки");
      const statusAllowed = selectedStatuses.length === 0
        || selectedStatuses.includes(deal.status_name || "Без статуса");
      return hasPromoCode && notInDb && pipelineAllowed && statusAllowed;
    });
  }, [allDeals, selectedPipelines, selectedStatuses]);
  const importableDeals = useMemo(
    () => filteredDeals.filter((deal) => !deal.exists_in_db && deal.promo_code),
    [filteredDeals]
  );
  const sortedDeals = useMemo(() => {
    const data = [...filteredDeals];
    data.sort((a, b) => {
      const direction = sortOrder === "asc" ? 1 : -1;
      if (sortField === "lead_id") {
        return ((Number(a.lead_id) || 0) - (Number(b.lead_id) || 0)) * direction;
      }
      if (sortField === "price") {
        return ((Number(a.price) || 0) - (Number(b.price) || 0)) * direction;
      }
      if (sortField === "created_ts") {
        return ((Number(a.created_ts) || 0) - (Number(b.created_ts) || 0)) * direction;
      }
      if (sortField === "status_id") {
        return ((Number(a.status_id) || 0) - (Number(b.status_id) || 0)) * direction;
      }
      if (sortField === "pipeline_name" || sortField === "status_name") {
        const left = String(a?.[sortField] || "").toLowerCase();
        const right = String(b?.[sortField] || "").toLowerCase();
        return left.localeCompare(right) * direction;
      }

      const left = String(a?.[sortField] || "").toLowerCase();
      const right = String(b?.[sortField] || "").toLowerCase();
      return left.localeCompare(right) * direction;
    });
    return data;
  }, [filteredDeals, sortField, sortOrder]);
  const pagedDeals = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedDeals.slice(start, start + itemsPerPage);
  }, [sortedDeals, currentPage, itemsPerPage]);
  const pagedSelectableDealIds = useMemo(
    () =>
      pagedDeals
        .filter((deal) => !deal.exists_in_db && deal.promo_code)
        .map((deal) => String(deal.lead_id)),
    [pagedDeals]
  );
  const allOnPageSelected = pagedSelectableDealIds.length > 0
    && pagedSelectableDealIds.every((id) => selectedDealIds.includes(id));
  const summaryRows = useMemo(() => {
    const grouped = new Map();
    for (const deal of importableDeals) {
      const promo = (deal.promo_code || "").trim();
      const key = promo.toLowerCase();
      if (!grouped.has(key)) {
        const partnerInfo = partnerByPromo[key] || {};
        const partnerName = partnerInfo.name || "";
        const payoutPercentage = Number(partnerInfo.payoutPercentage || 0);
        grouped.set(key, {
          promo_code: promo || "—",
          partner_name: partnerName || "Не найден",
          partner_found: Boolean(partnerName),
          payout_percentage: payoutPercentage,
          missing_count: 0,
          total_amount: 0,
          payout_amount: 0,
        });
      }
      const row = grouped.get(key);
      const amount = Number(deal.price || 0);
      row.missing_count += 1;
      row.total_amount += amount;
      row.payout_amount += amount * (Number(row.payout_percentage || 0) / 100);
    }
    return Array.from(grouped.values()).sort((a, b) => b.missing_count - a.missing_count);
  }, [importableDeals, partnerByPromo]);
  const summaryTotals = useMemo(
    () =>
      summaryRows.reduce(
        (acc, row) => {
          acc.deals += Number(row.missing_count || 0);
          acc.totalAmount += Number(row.total_amount || 0);
          acc.payoutAmount += Number(row.payout_amount || 0);
          return acc;
        },
        { deals: 0, totalAmount: 0, payoutAmount: 0 }
      ),
    [summaryRows]
  );

  useEffect(() => {
    if (!isAuthenticated || !hasRole(["admin", "manager"])) {
      setError("У вас нет доступа к этому разделу");
    } else {
      setError(null);
    }
  }, [hasRole, isAuthenticated]);

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await fetch(`${apiUrl}/admin/partners`, {
          credentials: "include",
        });
        if (!response.ok) return;
        const data = await response.json();
        const index = {};
        for (const partner of data || []) {
          const promo = String(partner.promo_code || "").trim().toLowerCase();
          if (!promo) continue;
          index[promo] = {
            name: partner.partner_name || "",
            payoutPercentage: Number(partner.payout_percentage || 0),
          };
        }
        setPartnerByPromo(index);
      } catch {
        // Summary still works without partner names.
      }
    };
    fetchPartners();
  }, [apiUrl]);

  useEffect(() => {
    if (!syncJobId) {
      return undefined;
    }

    const poll = async () => {
      try {
        const response = await fetch(`${apiUrl}/admin/sync/amocrm/status/${syncJobId}`, {
          credentials: "include",
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || "Ошибка получения статуса сверки");
        }
        setSyncStatus(data);
        if (data.status === "done") {
          setAllDeals(data.result?.all_deals || []);
          setMissingDeals(data.result?.missing_deals || []);
          setAvailablePipelines(data.result?.pipelines || []);
          setAvailableStatuses(data.result?.statuses || []);
          setSelectedPipelines(data.result?.pipelines || []);
          setSelectedStatuses(data.result?.statuses || []);
          setSelectedDealIds([]);
          setForceImportIds([]);
          setCompletedJobId(syncJobId);
          setCurrentPage(1);
          setSyncJobId(null);
        } else if (data.status === "failed") {
          setSyncError(data.message || "Сверка завершилась с ошибкой");
          setSyncJobId(null);
        }
      } catch (err) {
        setSyncError(err.message || "Ошибка при опросе статуса");
        setSyncJobId(null);
      }
    };

    poll();
    const timer = setInterval(poll, 2000);
    return () => clearInterval(timer);
  }, [syncJobId, apiUrl]);

  const handleRangeField = (field) => (event) => {
    const value = event.target.value;
    setPendingRange((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyRange = (event) => {
    event.preventDefault();
    const { startDate, endDate } = pendingRange;
    if (!startDate || !endDate) {
      setRangeError("Укажите обе даты.");
      return;
    }
    if (startDate > endDate) {
      setRangeError("Дата начала не может быть позже даты окончания.");
      return;
    }
    setRangeError(null);
    setDateRange(pendingRange);
  };

  const handleStartSync = async () => {
    setSyncStarting(true);
    setSyncError(null);
    setAllDeals([]);
    setMissingDeals([]);
    setAvailablePipelines([]);
    setAvailableStatuses([]);
    setSelectedPipelines([]);
    setSelectedStatuses([]);
    setSelectedDealIds([]);
    setForceImportIds([]);
    setImportMessage(null);
    setImportError(null);
    setImportSkipped([]);
    setCompletedJobId(null);
    setCurrentPage(1);
    setSyncStatus({
      status: "queued",
      progress: 0,
      message: "Подготовка к запуску сверки",
    });
    try {
      const response = await fetch(`${apiUrl}/admin/sync/amocrm/start`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          start_date: dateRange.startDate,
          end_date: dateRange.endDate,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Не удалось запустить сверку");
      }
      setSyncJobId(data.job_id);
    } catch (err) {
      setSyncError(err.message || "Ошибка запуска сверки");
      setSyncStatus(null);
    } finally {
      setSyncStarting(false);
    }
  };

  const toggleDealSelection = (leadId) => {
    const key = String(leadId);
    setSelectedDealIds((prev) =>
      prev.includes(key) ? prev.filter((id) => id !== key) : [...prev, key]
    );
  };

  const togglePageSelection = () => {
    if (allOnPageSelected) {
      setSelectedDealIds((prev) => prev.filter((id) => !pagedSelectableDealIds.includes(id)));
      return;
    }
    setSelectedDealIds((prev) => Array.from(new Set([...prev, ...pagedSelectableDealIds])));
  };

  const toggleNamedFilter = (value, selectedValues, setter) => {
    setter(
      selectedValues.includes(value)
        ? selectedValues.filter((item) => item !== value)
        : [...selectedValues, value]
    );
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortField(field);
    setSortOrder("asc");
  };

  const toggleForceImport = (leadId) => {
    const key = String(leadId);
    setForceImportIds((prev) =>
      prev.includes(key) ? prev.filter((id) => id !== key) : [...prev, key]
    );
  };

  const handlePromoCodeChange = (leadId, value) => {
    const leadKey = String(leadId);
    setAllDeals((prev) =>
      prev.map((deal) =>
        String(deal.lead_id) === leadKey
          ? { ...deal, promo_code: value }
          : deal
      )
    );
    setMissingDeals((prev) =>
      prev.map((deal) =>
        String(deal.lead_id) === leadKey
          ? { ...deal, promo_code: value }
          : deal
      )
    );
  };

  const handleImport = async (importAll) => {
    if (!completedJobId) {
      setImportError("Нет завершенной задачи сверки для импорта.");
      return;
    }
    const targetLeadIds = importAll
      ? importableDeals.map((deal) => String(deal.lead_id))
      : selectedDealIds;

    if (targetLeadIds.length === 0) {
      setImportError("Выбери хотя бы одну сделку.");
      return;
    }

    setImporting(true);
    setImportError(null);
    setImportMessage(null);
    setImportSkipped([]);
    try {
      const promoOverrides = allDeals.reduce((acc, deal) => {
        acc[String(deal.lead_id)] = (deal.promo_code || "").trim();
        return acc;
      }, {});

      const response = await fetch(`${apiUrl}/admin/sync/amocrm/import`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          job_id: completedJobId,
          import_all: false,
          lead_ids: targetLeadIds,
          promo_overrides: promoOverrides,
          force_import_lead_ids: forceImportIds,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Не удалось импортировать сделки");
      }

      const remainingLeadIds = new Set(
        (data.remaining_missing_deals || []).map((deal) => String(deal.lead_id))
      );
      const targetedLeadIdsSet = new Set(targetLeadIds.map(String));
      setAllDeals((prev) =>
        prev.map((deal) =>
          targetedLeadIdsSet.has(String(deal.lead_id)) && !remainingLeadIds.has(String(deal.lead_id))
            ? { ...deal, exists_in_db: true, possible_duplicate: false, duplicate_matches: [] }
            : deal
        )
      );
      setMissingDeals(data.remaining_missing_deals || []);
      setSelectedDealIds([]);
      setForceImportIds((prev) =>
        prev.filter((id) =>
          (data.remaining_missing_deals || []).some((deal) => String(deal.lead_id) === id)
        )
      );
      setCurrentPage(1);
      setImportMessage(
        `Импортировано: ${data.imported_count || 0}. Пропущено: ${data.skipped_count || 0}.`
      );
      setImportSkipped(data.skipped || []);
      setSyncStatus((prev) => {
        if (!prev) return prev;
        const nextResult = { ...(prev.result || {}) };
        nextResult.missing_in_db_count = (data.remaining_missing_deals || []).length;
        nextResult.missing_deals = data.remaining_missing_deals || [];
        return { ...prev, result: nextResult };
      });
    } catch (err) {
      setImportError(err.message || "Ошибка импорта");
    } finally {
      setImporting(false);
    }
  };

  const handleMergeDuplicate = async (leadId, targetOrderId) => {
    if (!completedJobId) {
      setImportError("Нет завершенной задачи сверки для слияния.");
      return;
    }
    const key = `${leadId}-${targetOrderId}`;
    setMergeLoadingKey(key);
    setImportError(null);
    setImportMessage(null);

    try {
      const promoOverrides = allDeals.reduce((acc, deal) => {
        acc[String(deal.lead_id)] = (deal.promo_code || "").trim();
        return acc;
      }, {});

      const response = await fetch(`${apiUrl}/admin/sync/amocrm/merge-duplicate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          job_id: completedJobId,
          lead_id: leadId,
          target_order_id: targetOrderId,
          promo_overrides: promoOverrides,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Не удалось выполнить слияние дубля");
      }

      setMissingDeals(data.remaining_missing_deals || []);
      setAllDeals((prev) =>
        prev.map((deal) =>
          String(deal.lead_id) === String(leadId)
            ? { ...deal, exists_in_db: true, possible_duplicate: false, duplicate_matches: [] }
            : deal
        )
      );
      setSelectedDealIds((prev) => prev.filter((id) => id !== String(leadId)));
      setForceImportIds((prev) => prev.filter((id) => id !== String(leadId)));
      setImportMessage(data.message || "Слияние выполнено");
      setSyncStatus((prev) => {
        if (!prev) return prev;
        const nextResult = { ...(prev.result || {}) };
        nextResult.missing_in_db_count = (data.remaining_missing_deals || []).length;
        nextResult.missing_deals = data.remaining_missing_deals || [];
        return { ...prev, result: nextResult };
      });
    } catch (err) {
      setImportError(err.message || "Ошибка слияния дубля");
    } finally {
      setMergeLoadingKey(null);
    }
  };

  if (error) {
    return (
      <Container className="page-loader text-center">
        <Alert variant="danger" className="shadow-sm">{error}</Alert>
        <Button className="mt-3 btn-rounded" onClick={() => navigate("/dashboard")}>
          Вернуться на Dashboard
        </Button>
      </Container>
    );
  }
  const fmtCurrency = (value) =>
    new Intl.NumberFormat("ru-RU", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(value || 0));

  return (
    <Container fluid="xl" className="compact py-3">
      <PageHeader
        title="Сверка amoCRM"
        subtitle="Проверка сделок с промокодом"
        right={<span className="chip chip-ghost">{dateRange.startDate} — {dateRange.endDate}</span>}
      />

      <section className="section mb-3">
        <Form className="row g-3 align-items-end" onSubmit={handleApplyRange}>
          <div className="col-md-4">
            <Form.Label>Начало периода</Form.Label>
            <Form.Control
              type="date"
              value={pendingRange.startDate}
              onChange={handleRangeField("startDate")}
            />
          </div>
          <div className="col-md-4">
            <Form.Label>Конец периода</Form.Label>
            <Form.Control
              type="date"
              value={pendingRange.endDate}
              onChange={handleRangeField("endDate")}
            />
          </div>
          <div className="col-md-4 d-flex gap-2">
            <Button type="submit" className="btn-rounded flex-grow-1">
              Применить период
            </Button>
            <Button
              type="button"
              className="btn-rounded"
              onClick={handleStartSync}
              disabled={syncStarting || !!syncJobId}
            >
              {syncStarting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Запуск...
                </>
              ) : (
                "Запустить сверку"
              )}
            </Button>
          </div>
        </Form>
        {rangeError && (
          <Alert variant="warning" className="mt-3 mb-0">
            {rangeError}
          </Alert>
        )}
      </section>

      <section className="section">
        {syncStatus && (
          <div className="mb-3">
            <div className="d-flex justify-content-between small text-muted mb-1">
              <span>{syncStatus.message || "Выполнение задачи"}</span>
              <span>{syncStatus.progress || 0}%</span>
            </div>
            <ProgressBar now={syncStatus.progress || 0} />
          </div>
        )}

        {syncError && (
          <Alert variant="danger" className="mb-3">
            {syncError}
          </Alert>
        )}

        {syncStatus?.status === "done" && (
          <Alert variant="success" className="mb-3">
            Сверка завершена. Всего сделок за период: {syncStatus.result?.all_deals_count || 0}
            {" · "}Не занесено в БД: {syncStatus.result?.missing_in_db_count || 0}
          </Alert>
        )}

        {importMessage && (
          <Alert variant="success" className="mb-3">
            {importMessage}
          </Alert>
        )}

        {importError && (
          <Alert variant="danger" className="mb-3">
            {importError}
          </Alert>
        )}

        {importSkipped.length > 0 && (
          <Alert variant="warning" className="mb-3">
            <div className="mb-1">Причины пропуска:</div>
            <ul className="mb-0">
              {importSkipped.slice(0, 5).map((item, idx) => (
                <li key={`${item.lead_id}-${idx}`}>
                  Сделка {item.lead_id || "?"}: {item.reason}
                </li>
              ))}
              {importSkipped.length > 5 && (
                <li>И ещё {importSkipped.length - 5}...</li>
              )}
            </ul>
          </Alert>
        )}

        {allDeals.length > 0 && (
          <div className="mb-3 p-3 rounded-3 border" style={{ background: "#f8fafc", borderColor: "#dbe3ef" }}>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <h6 className="mb-0">Фильтр сделок</h6>
              <span className="chip chip-ghost">
                Видно: {filteredDeals.length} · Доступно к импорту: {importableDeals.length}
              </span>
            </div>
            <div className="row g-3">
              <div className="col-lg-6">
                <div className="small text-muted mb-2">Воронки</div>
                <div className="d-flex flex-wrap gap-3">
                  {availablePipelines.map((pipelineName) => (
                    <Form.Check
                      key={pipelineName}
                      type="checkbox"
                      label={pipelineName}
                      checked={selectedPipelines.includes(pipelineName)}
                      onChange={() =>
                        toggleNamedFilter(pipelineName, selectedPipelines, setSelectedPipelines)
                      }
                    />
                  ))}
                </div>
              </div>
              <div className="col-lg-6">
                <div className="small text-muted mb-2">Статусы</div>
                <div className="d-flex flex-wrap gap-3">
                  {availableStatuses.map((statusName) => (
                    <Form.Check
                      key={statusName}
                      type="checkbox"
                      label={statusName}
                      checked={selectedStatuses.includes(statusName)}
                      onChange={() =>
                        toggleNamedFilter(statusName, selectedStatuses, setSelectedStatuses)
                      }
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {importableDeals.length > 0 && (
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Button
              className="btn-rounded"
              onClick={() => handleImport(false)}
              disabled={importing || selectedDealIds.length === 0}
            >
              {importing ? "Импорт..." : `Добавить выбранные (${selectedDealIds.length})`}
            </Button>
            <Button
              variant="outline-primary"
              className="btn-rounded"
              onClick={() => handleImport(true)}
              disabled={importing}
            >
              Добавить все подходящие ({importableDeals.length})
            </Button>
          </div>
        )}

        {summaryRows.length > 0 && (
          <div className="mb-3 p-3 rounded-3 border" style={{ background: "#f8fafc", borderColor: "#dbe3ef" }}>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
              <h6 className="mb-0">Сводка пропущенных сделок по промокодам</h6>
              <span className="chip chip-ghost">
                Сделок: {summaryTotals.deals} · Заказы: {fmtCurrency(summaryTotals.totalAmount)} EUR · Выплаты: {fmtCurrency(summaryTotals.payoutAmount)} EUR
              </span>
            </div>
            <div className="table-responsive">
              <Table hover size="sm" className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>Промокод</th>
                    <th>Партнёр</th>
                    <th>Сделок не занесено</th>
                    <th>Общая сумма заказов</th>
                    <th>Статус партнёра</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryRows.map((row) => (
                    <tr key={row.promo_code}>
                      <td>{row.promo_code}</td>
                      <td>{row.partner_name}</td>
                      <td>{row.missing_count}</td>
                      <td>{fmtCurrency(row.total_amount)} EUR</td>
                      <td>{row.partner_found ? "Найден" : "Партнёр не найден"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}

        <div className="table-responsive">
          <Table hover size="sm" className="align-middle mb-0">
            <thead>
              <tr>
                <th style={{ width: 44 }}>
                  <Form.Check
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={togglePageSelection}
                    aria-label="Выбрать сделки на странице"
                  />
                </th>
                <th
                  className={`th-sortable ${sortField === "lead_id" ? "th-active" : ""}`}
                  onClick={() => handleSort("lead_id")}
                >
                  ID сделки {sortField === "lead_id" && (sortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                </th>
                <th
                  className={`th-sortable ${sortField === "name" ? "th-active" : ""}`}
                  onClick={() => handleSort("name")}
                >
                  Название {sortField === "name" && (sortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                </th>
                <th
                  className={`th-sortable ${sortField === "promo_code" ? "th-active" : ""}`}
                  onClick={() => handleSort("promo_code")}
                >
                  Промокод {sortField === "promo_code" && (sortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                </th>
                <th
                  className={`th-sortable ${sortField === "price" ? "th-active" : ""}`}
                  onClick={() => handleSort("price")}
                >
                  Сумма {sortField === "price" && (sortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                </th>
                <th
                  className={`th-sortable ${sortField === "pipeline_name" ? "th-active" : ""}`}
                  onClick={() => handleSort("pipeline_name")}
                >
                  Воронка {sortField === "pipeline_name" && (sortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                </th>
                <th
                  className={`th-sortable ${sortField === "status_name" ? "th-active" : ""}`}
                  onClick={() => handleSort("status_name")}
                >
                  Статус {sortField === "status_name" && (sortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                </th>
                <th
                  className={`th-sortable ${sortField === "created_ts" ? "th-active" : ""}`}
                  onClick={() => handleSort("created_ts")}
                >
                  Создана {sortField === "created_ts" && (sortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredDeals.length > 0 ? (
                pagedDeals.map((deal) => {
                  return (
                  <React.Fragment key={deal.lead_id}>
                    <tr>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedDealIds.includes(String(deal.lead_id))}
                          onChange={() => toggleDealSelection(deal.lead_id)}
                          aria-label={`Выбрать сделку ${deal.lead_id}`}
                        />
                      </td>
                      <td>{deal.lead_id}</td>
                      <td>{deal.name || "—"}</td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="text"
                          value={deal.promo_code || ""}
                          disabled={deal.exists_in_db}
                          onChange={(e) => handlePromoCodeChange(deal.lead_id, e.target.value)}
                          placeholder="Промокод"
                        />
                      </td>
                      <td>{deal.price ?? "—"}</td>
                      <td>{deal.pipeline_name || "—"}</td>
                      <td>{deal.status_name || "—"}</td>
                      <td>{deal.created_at || "—"}</td>
                    </tr>
                  </React.Fragment>
                )})
              ) : (
                <tr>
                  <td colSpan={8} className="text-muted">
                    Запусти сверку, чтобы увидеть все сделки за период и отфильтровать их по воронкам и статусам.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {filteredDeals.length > 0 && (
          <div className="mt-3">
            <PaginationComponent
              currentPage={currentPage}
              totalItems={sortedDeals.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </div>
        )}
      </section>
    </Container>
  );
};

export default AmoSyncPage;
